'use client';

import clsx from 'clsx';
import { PageSection, Reveal, RevealItem, SectionHeading, BookDemoButton } from '@/components/ui';
import { fadeUp } from '@/lib/motion';
import s from './KeyCapabilitiesGrid.module.css';
import { useTranslation } from '@/hooks/useTranslation';

/*
 * Key Capabilities Grid - split layout (Retail) or centered 3x2 grid (Telecom).
 */
export default function KeyCapabilitiesGrid({
  heading,
  headingTitle,
  headingAccent,
  headingLine2,
  eyebrow,
  subtitle,
  cta,
  items,
  bg = 'tint',
  layout = 'split',
}) {
  const { t } = useTranslation();
  if (!items || items.length === 0) return null;

  if (layout === 'grid') {
    return (
      <PageSection bg={bg} className={s.section} aria-labelledby="keygrid-heading">
        <Reveal variant={fadeUp} className={s.gridHead}>
          <SectionHeading
            eyebrow={eyebrow ? t(eyebrow) : undefined}
            title={headingTitle ? t(headingTitle) : undefined}
            titleAccent={headingAccent ? t(headingAccent) : undefined}
            titleLine2={headingLine2 ? t(headingLine2) : undefined}
            size="lg"
            align="center"
            headingId="keygrid-heading"
          />
          {subtitle && <p className={clsx('type-body', s.gridSub)}>{t(subtitle)}</p>}
          {cta && cta.href && (
            <BookDemoButton
              label={t(cta.label)}
              href={cta.href}
              className={s.gridCta}
            />
          )}
        </Reveal>

        <Reveal stagger={0.08} className={s.cardGrid}>
          {items.map((it, i) => {
            const Icon = it.Icon;
            return (
              <RevealItem key={i} as="article" className={s.gridCard}>
                <span className={clsx(s.gridIcon, it.tileTone === 'alert' && s.gridIconAlert, it.tileTone === 'success' && s.gridIconSuccess)} aria-hidden="true">
                  {Icon && <Icon size={24} strokeWidth={1.8} />}
                </span>
                <h3 className={s.gridTitle}>{t(it.title)}</h3>
                <p className={s.gridBody}>{t(it.body)}</p>
              </RevealItem>
            );
          })}
        </Reveal>
      </PageSection>
    );
  }

  return (
    <PageSection bg={bg} className={s.section} aria-labelledby="keygrid-heading">
      <div className={s.inner}>
        <Reveal variant={fadeUp} className={s.lhs}>
          <h2 id="keygrid-heading" className="type-section-title">{t(heading)}</h2>
          {subtitle && <p className={clsx('type-body', s.sub)}>{t(subtitle)}</p>}
          {cta && cta.href && (
            <BookDemoButton
              label={t(cta.label)}
              href={cta.href}
              className={s.splitCta}
            />
          )}
        </Reveal>

        <Reveal stagger={0.08} className={s.rhs}>
          {items.map((it, i) => {
            const Icon = it.Icon;
            return (
              <RevealItem key={i} as="article" className={s.row}>
                <span className={s.rowIcon} aria-hidden="true">
                  {Icon && <Icon size={22} strokeWidth={1.8} />}
                </span>
                <div className={s.rowText}>
                  <h3 className={s.rowTitle}>{t(it.title)}</h3>
                  <p className={s.rowBody}>{t(it.body)}</p>
                </div>
              </RevealItem>
            );
          })}
        </Reveal>
      </div>
    </PageSection>
  );
}
